import React, { useState, useEffect , useCallback } from "react";
import {
    Box,
    Button,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import ErrorIcon from "@mui/icons-material/Error";
import {setPromoCode, setPromo, setCodeApplied, triggerGetBasket} from "../../redux/actions/setPromoCode";
import {useDispatch, useSelector} from "react-redux";
import {pushTermsPromoCode} from "../../gtm";

export default function PromoCodeInput() {

    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
    const headers = {
        'Content-Type': 'application/json'
    };

    const STATUS = {
        DEFAULT: "default",
        SUCCESS: "success",
        ERROR: "error",
    };

    const dispatch = useDispatch();

    const quoteResponse = useSelector(state => state.quoteResponse);
    const promoCode = useSelector(state => state.promoCode?.promoCode ?? '');
    const promo = useSelector(state => state.promoCode?.promo ?? '');

    const [status, setStatus] = useState(STATUS.DEFAULT);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const [errorMessage, setErrorMessage] = useState('');

    const applyPromo = () => {
        fetch(`${apiBaseUrl}/promoCode/getPromoCode?promoCode=${promoCode}`, {
            method: 'GET',
            headers: headers
        })
            .then(response => response.json())
            .then(data => {
                if (data.promoCode) {

                    dispatch(setPromo(data.promoCode));

                    const termsPromoCodeDetails = {
                        'DLV - promoCode': promoCode
                    };
                    // Push the Terms Promo Code event to the data layer
                    pushTermsPromoCode(termsPromoCodeDetails);
                    setStatus(STATUS.SUCCESS);

                    if(quoteResponse) {
                        handleApplyCode();
                    }
                } else {
                    setErrorMessage(data.message ?? 'Invalid promo code');
                    setStatus(STATUS.ERROR);
                }
            })
            .catch(error => {
                if(promoCode === "") {
                    setStatus(STATUS.DEFAULT);
                } else {
                    setStatus(STATUS.ERROR);
                }
            });

    };

    const handleApplyCode = useCallback(async (codeToApply = promoCode) => {

        if (!quoteResponse?.basket) {
            return;
        }

        const requestPayload = {
            basket: {
                id: quoteResponse.basket.guid,
                promoCode: codeToApply,
            },
        };

        const headers = {
            'Content-Type': 'application/json'
        };
        try {
            const response = await fetch(`${apiBaseUrl}/basket/updateBasket`, {
                method: 'POST',
                headers,
                body: JSON.stringify(requestPayload),
            });

            const data = await response.json();

            if (response.ok) {
                dispatch(setCodeApplied(true));
                dispatch(triggerGetBasket());
            } else {
                const notification = data.notifications?.[0];
                const errorMsg = notification?.message?.content ?? data.message ?? 'Failed to apply promo code';
                setErrorMessage(errorMsg);
                setStatus(STATUS.ERROR);
                dispatch(setCodeApplied(false));
                dispatch(triggerGetBasket());
            }
        } catch (error) {
            dispatch(setCodeApplied(false));
            dispatch(triggerGetBasket());
        }
    }, [apiBaseUrl, dispatch, promoCode, quoteResponse, STATUS.ERROR]);

    const resetCode = () => {
        dispatch(setPromoCode(null));
        dispatch(setPromo(null));

        const termsPromoCodeDetails = {
            'DLV - promoCode': ""
        };
        // Push the Terms Promo Code event to the data layer
        pushTermsPromoCode(termsPromoCodeDetails);
        setStatus(STATUS.DEFAULT);

        // Clear the promo off the basket server-side. Pass '' explicitly because
        // the setPromoCode(null) dispatch above hasn't propagated to state yet.
        handleApplyCode('');
    };

    const stylesByStatus = {
        [STATUS.DEFAULT]: {
            borderColor: "#B8D9A8",
            background: "#fff",
            iconBg: "#EEF7E8",
            iconColor: "#7CB342",
            titleColor: "#5A8D2B",
            applyBtnColor: "#7CB342",
            applyBtnColorHvr: "#7CB342",
        },
        [STATUS.SUCCESS]: {
            borderColor: "#B8D9A8",
            background: "#fff",
            iconBg: "#EEF7E8",
            iconColor: "#7CB342",
            titleColor: "#5A8D2B",
            applyBtnColor: "#7CB342",
            applyBtnColorHvr: "#7CB342",
        },
        [STATUS.ERROR]: {
            borderColor: "#F3B4B4",
            background: "#fff",
            iconBg: "#FDECEC",
            iconColor: "#E53935",
            titleColor: "#D32F2F",
            applyBtnColor: "#E53935",
            applyBtnColorHvr: "#E53935",
        },
    };

    const currentStyle = stylesByStatus[status];

    const renderIcon = () => {
        if (status === STATUS.ERROR) return <LocalOfferIcon fontSize="small" />;
        if (status === STATUS.SUCCESS) return <LocalOfferIcon fontSize="small" />;
        return <LocalOfferIcon fontSize="small" />;
    };

    useEffect(() => {
        console.log('checking status', promo);
        if (promo){
            setStatus('success');
        } else {
            setStatus('default');
        }
    }, [promo]);

    return (
        <Box
            sx={{
                border: `2px solid ${currentStyle.borderColor}`,
                borderRadius: "8px",
                padding: 1.5,
                backgroundColor: currentStyle.background
            }}
        >
            <Box display="flex" alignItems="flex-start" gap={1.5}>
                <Box
                    sx={{
                        minWidth: 36,
                        minHeight: 36,
                        borderRadius: "50%",
                        backgroundColor: currentStyle.iconBg,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: currentStyle.iconColor,
                        mt: 0.2,
                    }}
                >
                    {renderIcon()}
                </Box>

                <Box flex={1}>
                    <Typography
                        fontWeight={700}
                        fontSize={15.5}
                        color={currentStyle.titleColor}
                    >
                        Have a discount code?
                    </Typography>

                    <Typography fontSize={13} color="#555" mb={1.5}>
                        Enter your code to save on your transfer.
                    </Typography>

                    <Box
                        display="flex"
                        flexDirection={isMobile ? "column" : "row"}
                        gap={1.5}
                        alignItems={isMobile ? "stretch" : "center"}
                    >
                        <TextField
                            value={promoCode}
                            onChange={(e) => dispatch(setPromoCode(e.target.value))}
                            placeholder="Enter code"
                            size="small"
                            disabled={status === STATUS.SUCCESS}
                            sx={{
                                flex: 1,
                                "& .MuiOutlinedInput-root": {
                                    backgroundColor:
                                        status === STATUS.SUCCESS ? "#FAFAFA" : "#fff",
                                },
                            }}
                            InputProps={{
                                endAdornment:
                                    status === STATUS.SUCCESS ? (
                                        <CheckCircleIcon
                                            sx={{ color: "#43A047", fontSize: 20 }}
                                        />
                                    ) : null,
                            }}
                        />

                        {status === STATUS.SUCCESS ? (
                            <Button
                                variant="outlined"
                                onClick={resetCode}
                                sx={{
                                    minWidth: isMobile ? "100%" : 140,
                                    height: 40,
                                    borderColor: "#B7C6E6",
                                    color: "#265886",
                                    fontWeight: 600,
                                    textTransform: "none",
                                }}
                            >
                                Change code
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                onClick={applyPromo}
                                sx={{
                                    minWidth: isMobile ? "100%" : 150,
                                    height: 40,
                                    backgroundColor: currentStyle.applyBtnColor,
                                    fontWeight: 700,
                                    textTransform: "none",
                                    boxShadow: "none",
                                    "&:hover": {
                                        backgroundColor: currentStyle.applyBtnColorHvr,
                                        boxShadow: "none",
                                    },
                                }}
                            >
                                Apply discount
                            </Button>
                        )}
                    </Box>

                    {status === STATUS.ERROR && (
                        <Box display="flex" alignItems="center" gap={1} mt={1.5}>
                            <ErrorIcon sx={{ fontSize: 16, color: "#D32F2F" }} />
                            <Typography fontSize={14} color="#D32F2F">
                                {errorMessage}
                            </Typography>
                        </Box>
                    )}

                    {status === STATUS.SUCCESS && (
                        <Box display="flex" alignItems="center" gap={1} mt={1.5}>
                            <CheckCircleIcon sx={{ fontSize: 16, color: "#43A047" }} />
                            <Typography fontSize={14} color="#43A047">
                                Your discount has been applied below
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
}